import { GET_ACTIVITY_TODO_FEED } from "../../graphql/activity";
import { useQuery } from "@apollo/client/react";
import React, { use, useCallback, useMemo } from "react";
import { Activity, ActivityType, useActivityListItems } from "../../components/ActivityListView";
import { useListGroupRenderItem } from "../../components/ListGroup";
import { useI18n } from "../../hooks/i18n-hooks";
import { StackScreenProps } from "@react-navigation/stack";
import {
    getActivityFilterType,
    getActivityTitle,
    useActivityNavigationInfo,
} from "../../hooks/activities";
import { useFocusEffect } from "@react-navigation/native";
import EmptyScreen from "../../components/EmptyScreen";
import NoTodosImage from "../../assets/images/no-activities-image.svg";
import ScreenCard from "../ScreenCard";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import Loading from "../../components/Loading";
import { buildErrorHeadband } from "../ScreenCardHeadband";
import { useBusinessEmailVerificationHeadband } from "../../hooks/use-business-email-verification-headband";
import { UserActivitiesTabParamList } from "../../navigations/user/ActivitiesNavigation";
import { useTypeFilters } from "../../hooks/use-type-filters";

type TodosScreenProps = StackScreenProps<UserActivitiesTabParamList, "TodoList">;

const UserTodosScreen = ({ navigation }: TodosScreenProps) => {
    const { liftProfile, refreshUnreadProfilesActivities } = use(ProfileSwitcherManagerContext);

    const getActivityNavigationInfo = useActivityNavigationInfo<UserActivitiesTabParamList>();
    const i18n = useI18n();
    const emailVerificationHeadband = useBusinessEmailVerificationHeadband();
    const {
        data: todoFeed,
        loading,
        refetch: todoRefresh,
        error,
    } = useQuery(GET_ACTIVITY_TODO_FEED, {
        context: { liftProfile },
        fetchPolicy: "cache-and-network",
    });

    useFocusEffect(
        useCallback(() => {
            // Refresh the feed AND the tab-bar badge count together. The badge reads
            // getAllProfilesActivitiesInfo, which (unlike the feed) is not refetched on focus,
            // so a todo resolved outside an in-app mutation left a stale badge over an empty
            // list (BAS-2736). Backend count always equals the feed, so refetching both aligns them.
            void todoRefresh();
            void refreshUnreadProfilesActivities();
        }, [todoRefresh, refreshUnreadProfilesActivities]),
    );

    const getActionName = useCallback(
        (activityName: string) => {
            return i18n.t(`activities.actions.${activityName}`);
        },
        [i18n],
    );
    const activityTodos = useMemo(() => {
        const result = todoFeed?.getActivityTodoFeed
            .map((todo) => {
                return {
                    id: todo.id,
                    name: todo.activityName,
                    activityKey: todo.activityKey,
                    title: getActivityTitle(todo.title),
                    text: todo.text,
                    createdAt: todo.createdAt,
                    badgeUrl: todo.badgeUrl,
                    priority: todo.priority,
                    at: new Date(todo.createdAt),
                    actionText: getActionName(todo.activityName),
                    type: ActivityType.todo,
                    sourceName: todo.sourceName,
                };
            })
            .sort((a, b) => {
                if (a.priority !== b.priority) {
                    return b.priority - a.priority;
                }
                return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            });
        return result;
    }, [getActionName, todoFeed?.getActivityTodoFeed]);

    const onSelectTodo = useCallback(
        (activity: Activity) => {
            const navInfo = getActivityNavigationInfo(activity.name, activity.activityKey);
            if (navInfo) {
                navigation.navigate(navInfo);
            }
        },
        [getActivityNavigationInfo, navigation],
    );

    const refetch = useCallback(async () => {
        return Promise.all([todoRefresh(), refreshUnreadProfilesActivities()]);
    }, [refreshUnreadProfilesActivities, todoRefresh]);

    const isInitialLoading = loading && !todoFeed;

    const { filterComponent, filteredItems: filteredActivityTodos } = useTypeFilters({
        items: activityTodos,
        getFilterValue: (activity) => getActivityFilterType(activity.name),
        getLabel: (value, quantity) =>
            `${i18n.t(`screens.user.todos.filters.${value}`)} (${quantity})`,
    });

    const todoItems = useActivityListItems(filteredActivityTodos ?? [], {
        onSelectActivity: onSelectTodo,
    });
    const renderRow = useListGroupRenderItem(todoItems, { isItemsCheckable: false });

    // The list is virtualized when populated; loading/empty keep the ScrollView path. Unlike
    // some screens, an error here does NOT hide the list (it only shows a headband), so `error`
    // is intentionally absent from `showList`.
    const showList = !isInitialLoading && (filteredActivityTodos?.length ?? 0) > 0;

    return (
        <>
            {filterComponent}
            <ScreenCard
                refresh={refetch}
                isContentContainerFlex={
                    !error && (filteredActivityTodos?.length === 0 || isInitialLoading)
                }
                headband={
                    error
                        ? buildErrorHeadband(i18n.t("screens.user.todos.cannot_load_todos"))
                        : emailVerificationHeadband
                }
                virtualizedItems={showList ? todoItems : undefined}
                renderItem={showList ? renderRow : undefined}
            >
                {showList ? undefined : isInitialLoading ? (
                    <Loading />
                ) : !filteredActivityTodos || filteredActivityTodos.length === 0 ? (
                    <EmptyScreen
                        image={NoTodosImage}
                        text={i18n.t("screens.user.todos.no_activity")}
                    />
                ) : null}
            </ScreenCard>
        </>
    );
};

export default UserTodosScreen;
